import React, {
  SyntheticEvent,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { useIsAdmin, useSession } from 'queries';
import { Chip, MenuItem, MenuWindow, SquareButton } from '@app/core';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import classNames from 'classnames';

interface Props {
  onChange: (value: string[]) => any;
  value?: string[];
  sessionId?: string;
  large?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  iconColor: {
    color: theme.colors.purpleAccessible,
  },
}));

export const TagSelectButton = ({
  onChange,
  value,
  sessionId,
  large,
}: Props) => {
  const isAdmin = useIsAdmin();
  const classes = useStyles();

  const selectedTags = useMemo(() => {
    return value || [];
  }, [value]);

  const id = useId();

  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);
  const { data: session } = useSession(sessionId);
  const tags = session ? session.availableTags || [] : [];

  const onClick = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      setAnchorElmnt(event.currentTarget);
    },
    [setAnchorElmnt],
  );

  const onCloseSelector = useCallback(() => {
    setAnchorElmnt(null);
  }, [setAnchorElmnt]);

  const onAddTag = useCallback(
    (tagId: string) => {
      if (selectedTags.includes(tagId)) {
        onChange(selectedTags.filter((t) => t !== tagId));
        return;
      }

      onChange([...selectedTags, tagId]);
    },
    [onChange, selectedTags],
  );

  const color: Colors = useMemo(() => {
    if (!selectedTags?.length) {
      return 'purple';
    }
    const id = selectedTags[0];

    return tags.find((t) => t.id === id)?.color || 'purple';
  }, [selectedTags, tags]);

  if (!isAdmin || !tags.length) {
    return null;
  }

  return (
    <>
      <SquareButton
        color={color}
        icon={classNames('ri-price-tag-3-line', classes.iconColor)}
        onClick={onClick}
        variant={!!selectedTags.length ? 'muted' : 'transparent'}
        large={large}
      />
      <MenuWindow
        open={!!anchorElmnt}
        onClose={onCloseSelector}
        anchorEl={anchorElmnt}
        id={id}
      >
        {tags.map((tag) => (
          <MenuItem
            value={tag.id}
            key={tag.id}
            selected={selectedTags.includes(tag.id)}
            onSelect={onAddTag}
          >
            <Chip
              customColor={tag.color}
              key={tag.id}
              variant="selectable"
              selected={selectedTags.includes(tag.id)}
            >
              {tag.name}
            </Chip>
          </MenuItem>
        ))}
      </MenuWindow>
    </>
  );
};
